import { useCallback, useEffect, useRef, useState } from 'react'

const defaultConfig: IntersectionObserverInit = {
  root: null,
  rootMargin: '0px 0px 0px 0px',
  threshold: [0, 1],
}

export const useIntersectionObserver = (
  callback: IntersectionObserverCallback,
  config: IntersectionObserverInit = defaultConfig
) => {
  const { root, rootMargin, threshold } = config

  const [node, setNode] = useState<HTMLElement | null>(null)

  const callbackRef = useRef<IntersectionObserverCallback>(callback)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    callbackRef.current = callback
  })

  const onIntersect = useCallback<IntersectionObserverCallback>((...args) => {
    return !!callbackRef.current && callbackRef.current(...args)
  }, [])

  useEffect(() => {
    if (node) {
      observerRef.current = new IntersectionObserver(onIntersect, config)

      observerRef.current.observe(node)
    }

    return () => {
      if (observerRef.current !== null) {
        observerRef.current.disconnect()
      }
    }
  }, [node, onIntersect, root, rootMargin, threshold])

  const nodeRef = useCallback((node: HTMLElement | null) => {
    setNode(node)
  }, [])

  return [nodeRef]
}
